import { useState, useMemo, useEffect, useRef } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { DEFAULT_FLAGS, matchLabel, flagString } from "./regexUtils";
import type { RegexRequest, RegexResponse } from "./regex.worker";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Tests a regular expression against sample text in real time, highlighting matches directly within the text as you build and refine the pattern — rather than leaving you to figure out where a match landed from a raw list of index numbers.`,
  how: `Enter your regular expression and some test text. Matches are highlighted directly in the text below as you type, with a plain-language explanation if the pattern is invalid, and each match's captured groups (the parts inside parentheses) shown separately when your pattern uses them.`,
  tip: `Build complex patterns incrementally — start with a simple pattern that matches the basic case, confirm it works, then add complexity piece by piece rather than trying to write the whole expression at once. If a pattern isn't matching what you expect, check the highlighted text first — seeing exactly what it caught (and didn't) is usually faster than staring at the pattern alone.`,
};

type Match = { match: string; index: number; groups: string[]; named: Record<string, string> };
const TIMEOUT_MS = 1500;
const FLAG_DEFS: [string, string][] = [
  ["g", "Global (g)"],
  ["i", "Ignore case (i)"],
  ["m", "Multiline (m)"],
  ["s", "Dot matches newline (s)"],
  ["u", "Unicode mode (u): enables \\p{…} and full code points"],
];

export default function RegexTester() {
  const [pattern, setPattern] = useState("");
  const [text, setText] = useState("");
  const [on, setOn] = useState<Record<string, boolean>>(DEFAULT_FLAGS);
  const globalFlag = on.g;
  const currentFlags = flagString(on);

  const [matches, setMatches] = useState<Match[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const workerRef = useRef<Worker | null>(null);
  const reqId = useRef(0);

  useEffect(() => {
    const id = ++reqId.current;
    setTimedOut(false);
    if (!pattern || !text) {
      setMatches([]);
      setError(null);
      setTruncated(false);
      return;
    }
    // Debounce, then run in a fresh worker so a stuck one can simply be terminated.
    const start = setTimeout(() => {
      workerRef.current?.terminate();
      const w = new Worker(new URL("./regex.worker.ts", import.meta.url), { type: "module" });
      workerRef.current = w;
      const killer = setTimeout(() => {
        if (id !== reqId.current) return;
        w.terminate();
        setMatches([]);
        setError(null);
        setTimedOut(true);
      }, TIMEOUT_MS);
      w.onmessage = (e: MessageEvent<RegexResponse>) => {
        clearTimeout(killer);
        if (e.data.id !== reqId.current) return;
        if (e.data.ok) {
          setMatches(e.data.matches);
          setTruncated(e.data.truncated);
          setError(null);
        } else {
          setMatches([]);
          setError(e.data.error);
        }
        w.terminate();
      };
      w.postMessage({ id, pattern, flags: currentFlags, text, first: !globalFlag } satisfies RegexRequest);
    }, 250);
    return () => clearTimeout(start);
  }, [pattern, text, currentFlags, globalFlag]);

  useEffect(() => () => workerRef.current?.terminate(), []);

  // Builds the highlighted-text view: alternating plain and highlighted
  // spans, computed directly from the same match positions already
  // found above (not a second, separate regex pass that could disagree
  // with the match list) -- this is what actually makes the earlier
  // "highlighting matches" claim genuinely true, rather than just a
  // flat list of index numbers someone has to manually locate in the
  // text themselves.
  const highlightedSegments = useMemo(() => {
    if (matches.length === 0) return [{ text, highlighted: false }];
    const segments: { text: string; highlighted: boolean }[] = [];
    let cursor = 0;
    for (const m of matches) {
      if (m.index > cursor) segments.push({ text: text.slice(cursor, m.index), highlighted: false });
      segments.push({ text: m.match, highlighted: true });
      cursor = m.index + m.match.length;
    }
    if (cursor < text.length) segments.push({ text: text.slice(cursor), highlighted: false });
    return segments;
  }, [matches, text]);

  const { blocked } = useLogToolUsage(true, 3000);


  return (
    <ToolContainer>
      <div className="space-y-4">
        <div><Label>Regular Expression</Label><Input aria-label="Regular Expression" value={pattern} onChange={e => setPattern(e.target.value)} placeholder="Enter regex pattern..." className="font-mono" /></div>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {FLAG_DEFS.map(([k, label]) => (
            <div key={k} className="flex items-center gap-2">
              <Switch id={`rx-flag-${k}`} aria-label={label} checked={on[k]} onCheckedChange={v => setOn({ ...on, [k]: v })} />
              <label htmlFor={`rx-flag-${k}`} className="text-sm">{label}</label>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">Flags in use: <code>/{currentFlags || "(none)"}</code>{!on.g && " (only the first match is shown without g)"}</p>
        <div><Label>Test String</Label><Textarea aria-label="Test String" value={text} onChange={e => setText(e.target.value)} placeholder="Enter text to test against..." className="min-h-[120px] font-mono" /></div>

        {error && (
          <div className="p-3 rounded-lg border border-destructive/30 bg-destructive/5 text-sm text-destructive">
            Pattern error: {error}
          </div>
        )}

        {timedOut && (
          <NoticeBox className="text-sm" role="alert">
            This pattern took longer than {TIMEOUT_MS / 1000} seconds and was stopped. It may cause catastrophic backtracking
            (for example nested repetition like <code>(a+)+</code>). Simplify it or make the parts less ambiguous.
          </NoticeBox>
        )}
        {text && !error && !timedOut && !blocked && (
          <div>
            <Label className="mb-2 block">Highlighted Text</Label>
            <div className="p-4 rounded-lg border bg-card font-mono text-sm whitespace-pre-wrap break-words">
              {highlightedSegments.map((seg, i) =>
                seg.highlighted
                  ? <mark key={i} className="bg-primary/25 text-foreground rounded px-0.5">{seg.text}</mark>
                  : <span key={i}>{seg.text}</span>
              )}
            </div>
          </div>
        )}

        <div className="p-4 rounded-lg border bg-card">
          <p className="text-sm font-medium mb-2">{blocked ? "— blocked at today's usage limit —" : `${matches.length}${truncated ? "+" : ""} match${matches.length === 1 ? "" : "es"} found`}</p>
          {!blocked && matches.length > 0 && (
            <div className="space-y-1.5">
              {matches.slice(0, 20).map((m, i) => (
                <div key={i} className="text-sm font-mono">
                  <span className="text-muted-foreground">#{i + 1}</span> <span className={m.match === "" ? "bg-primary/10 px-1 rounded italic text-muted-foreground" : "bg-primary/10 px-1 rounded"}>{matchLabel(m.match)}</span> <span className="text-xs text-muted-foreground">at index {m.index}</span>
                  {(m.groups.length > 0 || Object.keys(m.named).length > 0) && (
                    <span className="text-xs text-muted-foreground ml-2">
                      groups: {m.groups.map((g, gi) => `$${gi + 1}="${g}"`).join(", ")}
                      {Object.entries(m.named).map(([k, v]) => `, ${k}="${v}"`).join("")}
                    </span>
                  )}
                </div>
              ))}
              {matches.length > 20 && (
                <p className="text-xs text-muted-foreground pt-1">...and {matches.length - 20} more (showing the first 20)</p>
              )}
            </div>
          )}
        </div>
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
